import { Box, Flex, Heading, HStack, Stack, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import * as m from 'motion/react-m';

import { Button, CalendarTimeIcon, CommentIcon, MicOnIcon, PeopleIcon } from '#features/ui';
import { PhoneFrame } from './PhoneFrame';

/** slug -> real game icon, color, photo and name, supplied from the games query. */
export type GameInfo = {
  icon?: string | null;
  iconColor?: string | null;
  image?: string | null;
  name?: string | null;
  slug?: string | null;
};
export type GameIconMap = Record<string, GameInfo>;

type SessionKind = 'none' | 'scheduled' | 'live';

type MockPost = {
  gameSlug: string;
  gameName: string;
  fallbackColor: string;
  platform: string;
  name: string;
  flag: string;
  age: number;
  gender: string;
  languages: string;
  text: string;
  tags: string[];
  time: string;
  comments: number;
  session: SessionKind;
  sessionTime?: string;
  slots?: string;
};

// Ordered most-actionable first: a live session up top (visible immediately),
// then an upcoming scheduled session, then an open post with no session.
// Copy + tags are written to match each game.
const POSTS: MockPost[] = [
  {
    gameSlug: 'call-of-duty-warzone-2',
    gameName: 'Warzone',
    fallbackColor: '#207c68',
    platform: 'PC',
    name: 'Kai',
    flag: '🇺🇸',
    age: 24,
    gender: 'Male',
    languages: 'English',
    text: "Rebirth Resurgence, need two more for quads. Comms on - we're chaining wins right now.",
    tags: ['Resurgence', 'Ranked', 'Quads', 'Diamond'],
    time: '5 minutes ago',
    comments: 6,
    session: 'live',
    slots: '2/4',
  },
  {
    gameSlug: 'fortnite',
    gameName: 'Fortnite',
    fallbackColor: '#58c6e4',
    platform: 'PlayStation',
    name: 'Lena',
    flag: '🇩🇪',
    age: 21,
    gender: 'Female',
    languages: 'English, German',
    text: 'Ranked grind to Unreal tonight. Looking for a duo who builds fast and keeps a cool head.',
    tags: ['Ranked', 'Build', 'Unreal', 'Duos'],
    time: '9 minutes ago',
    comments: 4,
    session: 'scheduled',
    sessionTime: '21:00',
    slots: '1/2',
  },
  {
    gameSlug: 'apex-legends',
    gameName: 'Apex Legends',
    fallbackColor: '#fe634e',
    platform: 'Xbox',
    name: 'Tim',
    flag: '🇬🇧',
    age: 22,
    gender: 'Male',
    languages: 'English, French',
    text: 'Wraith main pushing for Diamond. Want chill teammates who rotate smart and actually ping.',
    tags: ['Ranked', 'Diamond', 'Wraith', 'Skill Improve'],
    time: '18 minutes ago',
    comments: 3,
    session: 'none',
  },
];

const GameIcon = ({ icon, color }: { icon?: string | null; color: string }) => {
  if (icon) {
    return (
      <Box
        w="18px"
        h="18px"
        flexShrink={0}
        bgColor={color}
        style={{
          mask: `url(${icon}) center / contain no-repeat`,
          WebkitMask: `url(${icon}) center / contain no-repeat`,
        }}
      />
    );
  }
  return (
    <Box
      w="18px"
      h="18px"
      borderRadius="4px"
      flexShrink={0}
      style={{ background: `linear-gradient(135deg, ${color} 0%, #7C3AED 130%)` }}
    />
  );
};

const Tag = ({ children }: { children: React.ReactNode }) => (
  <Flex
    alignItems="center"
    px={2}
    py={1}
    borderRadius="md"
    bgColor="bg.emphasized"
    fontSize="11px"
    fontWeight="600"
    color="fg.emphasized"
    whiteSpace="nowrap"
    flexShrink={0}
  >
    {children}
  </Flex>
);

const zeroPad = (n: number) => String(n).padStart(2, '0');

// Matches the product's Timer: hours shown only when present -> "2:10:20" / "47:20".
const formatCountdown = (total: number) => {
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${hours ? `${hours}:` : ''}${zeroPad(minutes)}:${zeroPad(seconds)}`;
};

/** Live countdown to session end - ticks down each second. 2h session started 5 min ago -> 1:55:00. */
const LiveTimer = ({ start = 6900 }: { start?: number }) => {
  const [seconds, setSeconds] = useState(start);

  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => (s <= 1 ? start : s - 1)), 1000);
    return () => clearInterval(id);
  }, [start]);

  return (
    <HStack gap={1}>
      <Text fontSize="12px" fontWeight="700" color="fg.emphasized" fontVariantNumeric="tabular-nums">
        {formatCountdown(seconds)}
      </Text>
      <Text fontSize="10px" color="fg.muted">
        left
      </Text>
    </HStack>
  );
};

const PeoplePill = ({ slots }: { slots: string }) => (
  <Flex alignItems="center" gap={1} bgColor="bg.muted" borderRadius="full" px={2} py={0.5} fontSize="11px" fontWeight="700" color="fg.emphasized">
    <Flex fontSize="12px">
      <PeopleIcon />
    </Flex>
    {slots}
  </Flex>
);

const SessionStrip = ({ post, color }: { post: MockPost; color: string }) => {
  if (post.session === 'scheduled') {
    return (
      <Flex
        alignItems="center"
        gap={2}
        px={2.5}
        py={1.5}
        borderRadius="lg"
        style={{ background: `linear-gradient(135deg, ${color}33 0%, rgba(75,80,230,0.26) 100%)` }}
      >
        <Flex alignItems="center" gap={1.5} fontSize="12px" fontWeight="700" color="fg.emphasized">
          <Flex fontSize="14px">
            <CalendarTimeIcon />
          </Flex>
          Today · {post.sessionTime}
        </Flex>
        <Box ml="auto">{post.slots && <PeoplePill slots={post.slots} />}</Box>
      </Flex>
    );
  }

  if (post.session === 'live') {
    return (
      <Flex
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        rowGap={1.5}
        columnGap={2}
        px={2}
        py={1.5}
        borderRadius="lg"
        style={{ background: `linear-gradient(135deg, rgba(34,197,94,0.20) 0%, ${color}33 100%)` }}
      >
        <Flex alignItems="center" gap={1.5}>
          <Box w="7px" h="7px" borderRadius="full" bg="#22c55e" style={{ boxShadow: '0 0 8px #22c55e' }} />
          <Text fontSize="10px" fontWeight="800" color="#22c55e" letterSpacing="0.06em">
            LIVE
          </Text>
          <LiveTimer />
        </Flex>
        {post.slots && <PeoplePill slots={post.slots} />}
      </Flex>
    );
  }

  return null;
};

const PostCardMock = ({ post, gameIcons }: { post: MockPost; gameIcons: GameIconMap }) => {
  const resolved = gameIcons[post.gameSlug];
  const color = resolved?.iconColor || post.fallbackColor;

  return (
    <Box
      w="100%"
      bgColor="brand.700"
      borderRadius="lg"
      border="1px solid"
      borderColor="border.subtle"
      overflow="hidden"
      flexShrink={0}
    >
      {/* Header */}
      <Flex px={2.5} bgColor="bg.emphasized" w="100%" h="38px" alignItems="center" gap={2}>
        <GameIcon icon={resolved?.icon} color={color} />
        <Text fontSize="12px" fontWeight="700" color="fg.emphasized" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" flex={1} minW={0}>
          {post.gameName}
          <Text as="span" fontWeight="400" color="fg.muted" ml={1.5}>
            {post.platform}
          </Text>
        </Text>
      </Flex>

      {/* Content */}
      <Flex direction="column" p={2.5} gap={2.5} w="100%">
        {/* User */}
        <Flex alignItems="flex-start" gap={2.5}>
          <Flex
            w="36px"
            h="36px"
            borderRadius="full"
            alignItems="center"
            justifyContent="center"
            fontSize="14px"
            fontWeight="800"
            color="white"
            flexShrink={0}
            position="relative"
            style={{ background: `linear-gradient(135deg, ${color} 0%, #8E7BFF 135%)` }}
          >
            {post.name[0]}
            <Box
              position="absolute"
              bottom="0"
              right="0"
              w="9px"
              h="9px"
              borderRadius="full"
              bg="#22c55e"
              border="2px solid"
              borderColor="brand.700"
            />
          </Flex>
          <Stack gap={0.5} flex={1} minW={0}>
            <Heading fontSize="15px" display="flex" alignItems="center" gap={1.5} color="fg.emphasized" lineHeight="1">
              {post.name}
              <Text as="span" fontSize="13px">
                {post.flag}
              </Text>
            </Heading>
            <HStack gap={1} fontSize="11px" color="fg.muted" flexWrap="wrap">
              <Text>{post.age} years</Text>
              <Text>&middot;</Text>
              <Text>{post.gender}</Text>
              <Text>&middot;</Text>
              <Flex fontSize="13px" alignItems="center">
                <MicOnIcon />
              </Flex>
              <Text>&middot;</Text>
              <Text>{post.languages}</Text>
            </HStack>
          </Stack>
        </Flex>

        {/* Text */}
        <Box bg="bg.muted" borderRadius="lg" px={3} py={2}>
          <Text
            fontSize="12px"
            color="fg.emphasized"
            lineHeight="1.5"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {post.text}
          </Text>
        </Box>

        {/* Tags */}
        <HStack gap={1.5} overflow="hidden">
          {post.tags.map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </HStack>

        {/* Session */}
        <SessionStrip post={post} color={color} />

        {/* Footer */}
        <HStack justifyContent="space-between" w="100%">
          <Text fontSize="11px" color="fg.muted">
            {post.time}
          </Text>
          <HStack gap={2.5}>
            <HStack gap={1} color="fg.muted">
              <Flex fontSize="15px">
                <CommentIcon />
              </Flex>
              <Text fontSize="12px" fontWeight="700">
                {post.comments}
              </Text>
            </HStack>
            <Button size="xs" minW="52px" fontSize="12px">
              Go
            </Button>
          </HStack>
        </HStack>
      </Flex>
    </Box>
  );
};

const DiscoverFeed = ({ gameIcons }: { gameIcons: GameIconMap }) => (
  <Flex direction="column" h="100%" position="relative" overflow="hidden">
    <Box position="absolute" top={0} left={0} right={0} h="42px" zIndex={4} pointerEvents="none" background="linear-gradient(to bottom, hsl(228,11.6%,7%) 30%, transparent)" />
    <Box position="absolute" bottom={0} left={0} right={0} h="24px" zIndex={4} pointerEvents="none" background="linear-gradient(to top, hsl(228,11.6%,7%) 30%, transparent)" />

    <Box asChild>
      <m.div
        initial={{ y: 0 }}
        animate={{ y: [0, 0, -420, -420, 0] }}
        transition={{ duration: 19, times: [0, 0.12, 0.5, 0.76, 1], repeat: Infinity, ease: 'easeInOut' }}
        style={{ paddingTop: 40, paddingLeft: 12, paddingRight: 12, paddingBottom: 12 }}
      >
        <Stack gap={2.5}>
          {POSTS.map((post) => (
            <PostCardMock key={post.name} post={post} gameIcons={gameIcons} />
          ))}
        </Stack>
      </m.div>
    </Box>
  </Flex>
);

export const DiscoverMock = ({ gameIcons }: { gameIcons: GameIconMap }) => (
  <PhoneFrame>
    <DiscoverFeed gameIcons={gameIcons} />
  </PhoneFrame>
);
